import { useQuery } from "@apollo/client/react";
import { ArrayUtil } from "@coten-inc/utils";
import { useMemo } from "react";

import {
  GetCategoriesWithPropertiesDocument,
  type GetCategoriesWithPropertiesQuery,
  type GetCategoriesWithPropertiesQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { SchemaDesignerCategoryMapper } from "~/features/dataImportProjects/hooks";

import type { SchemaDesignerCategory } from "./types";

type CategoryTypeId = "entity" | "relation";

export function useCategorySearch(typeId: CategoryTypeId, searchTerm: string) {
  const variables = useMemo(() => {
    return {
      where: {
        deactivatedAt: { _isNull: true },
        typeId: { _eq: typeId },
        ...(searchTerm && { name: { _ilike: `%${searchTerm}%` } }),
      },
    } satisfies GetCategoriesWithPropertiesQueryVariables;
  }, [searchTerm, typeId]);

  const { data, loading } = useQuery<GetCategoriesWithPropertiesQuery, GetCategoriesWithPropertiesQueryVariables>(
    GetCategoriesWithPropertiesDocument,
    {
      variables,
    },
  );

  const categories: SchemaDesignerCategory[] = useMemo(() => {
    if (!data?.categories) {
      return [];
    }
    return data.categories
      .map((cat) => {
        if (cat.typeId === "entity") {
          return SchemaDesignerCategoryMapper.categoryCandidate.entity.toModel(cat);
        } else {
          return SchemaDesignerCategoryMapper.categoryCandidate.relation.toModel(cat);
        }
      })
      .filter(ArrayUtil.notEmptyPredicate);
  }, [data, typeId]);

  return { categories, loading } as const;
}
